import React, { useState } from 'react';
import { Paper } from '@mui/material';
import DynamicTable from '../../components/Table/Table';
import { triggerNotification } from '../../utils/NotificationContext';
import * as XLSX from 'xlsx';
import { useSelector } from 'react-redux';
import UploadModal from '../../components/Modals/Upload';
import { bulkUploadFile } from './services';

const DecisionTable = ({
  data,
  isTableLoading,
  checkedRows,
  setCheckedRows,
  columns,
  tableName,
  showCheckbox,
  currentVotingTeam,
  setUploadStatusMsg,
  isUploadDownloadDisabled,
  currentActiveCycle,
  currentActiveCycleID,
  userType,
  userLevelUpload,
  isCompleted,
  isDecision
}) => {
  const selectedBu = useSelector((state) => state?.cycle?.selectedBu);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const isRegionSpecific = (label) => {
    const exceptions = ['STEERING TEAM', 'Region Level','FUNCTIONAL STAKEHOLDERS COLLATED COMMENTS'];
    const lowerLabel = label.toLowerCase();

    return (
      !exceptions.some((exc) => lowerLabel.startsWith(exc.toLowerCase())) &&
      (lowerLabel.endsWith('agree steering team reco') ||
        lowerLabel.endsWith('recommendation') ||
        lowerLabel.endsWith('comments'))
    );
  };
  const cleanedFields = columns.filter((field) => !isRegionSpecific(field.label));
  const handleDownload = async () => {
    try {
      const content = data;
      const columnIds = columns.map((column) => column.id);

      const transformedContent = content.map((row) => {
        const newRow = {};
        columnIds.forEach((columnId) => {
          const upperCaseColumnId = columnId.toUpperCase();
          if (columnId) {
            if (typeof row[columnId] === 'number') {
              newRow[upperCaseColumnId] = row[columnId];
            } else {
              newRow[upperCaseColumnId] = row[columnId] !== undefined ? String(row[columnId]) : '';
            }
          }
          if (upperCaseColumnId === 'TAGGEDUSERS') {
            // If the columnId is taggedUsers, extract User Emails and join them into a string
            newRow[upperCaseColumnId] =
              row[columnId]?.map((user) => user.UserEmail).join(', ') || ''; // Join User Emails or return empty string
          } else if (
            upperCaseColumnId === 'REQUESTED INPUT FROM' ||
            upperCaseColumnId === 'FUNCTIONAL TEAM COMMENTS' ||
            upperCaseColumnId === 'COLLATED COMMENTS'
          ) {
            // If the columnId is REQUESTED INPUT FROM, flatten the array and join names into a string
            newRow[upperCaseColumnId] = row[columnId]?.join(', ') || ''; // Join names or return empty string
          } else if (
            upperCaseColumnId === 'AGREEDISAGREESTATUS' ||
            upperCaseColumnId === 'STEERING TEAM AGREEMENT' ||
            upperCaseColumnId.includes('AGREE STEERING TEAM RECO') ||
            upperCaseColumnId.includes('COMPLETIONSTATUS')
          ) {
            // Transform boolean value to "Like" or "Dislike"
            if (row[columnId] === null) {
              newRow[upperCaseColumnId] = '';
            } else if (row[columnId] === false) {
              newRow[upperCaseColumnId] = 'Disagree';
            } else {
              newRow[upperCaseColumnId] = 'Agree';
            }
          } else if (upperCaseColumnId === 'FUNCTIONAL TEAM VOTES') {
            // Convert functional team votes to a comma-separated string
            const upvotes = row[columnId]?.functionalteamupvotes || 0;
            const downvotes = row[columnId]?.functionalteamdownvotes || 0;
            newRow[upperCaseColumnId] = `${upvotes} Agree, ${downvotes} Disagree`; // Create a string with upvotes and downvotes
          } else if (row[columnId] === null) {
            newRow[upperCaseColumnId] = '';
          }
        });
        delete newRow['REGIONAL TEAM VOTES'];
        delete newRow['INPUTS FROM REGIONAL TEAMS'];
        return newRow;
      });
      const updateKeyNames = (data) => {
        return data.map((item) => {
          if(isDecision || isCompleted) {
            const { DECISION, EFFECTIVEDATE, OWNEREMAIL, INSTRUCTIONCOMMENT, ...rest } = item; // Extract keys to be renamed
            delete rest['EFFECTIVE DATE'];
            delete rest['OWNER'];
            delete rest['COMMENT'];
            if(isDecision) {
              return {
                ...rest, // Retain all other fields
                'DECISION(MAX 24 CHAR)': DECISION, // Add the renamed key
                'EFFECTIVE DATE(YYYY-MM-DD)': EFFECTIVEDATE, // Add the renamed key
                OWNER: OWNEREMAIL,
                COMMENT: INSTRUCTIONCOMMENT
              };
            }
            else {
              return {
                ...rest, // Retain all other fields
                'DECISION': DECISION, // Add the renamed key
                'EFFECTIVE DATE': EFFECTIVEDATE, // Add the renamed key
                OWNER: OWNEREMAIL,
                COMMENT: INSTRUCTIONCOMMENT
              };
            }
          } else {
            if (currentVotingTeam === 'Steering Reco') {
              const { AGREEDISAGREESTATUS, RECOMMENDATION, COMMENT, TAGGEDUSERS, ...rest } = item; // Extract keys to be renamed
              return {
                ...rest, // Retain all other fields
                'AGREE ACTION RECO': AGREEDISAGREESTATUS, // Add the renamed key
                'RECOMMENDATION(MAX 24 CHAR)': RECOMMENDATION, // Add the renamed key
                COMMENT,
                TAGGEDUSERS
              };
            } else if (currentVotingTeam === 'Functional Feedback') {
              const { AGREEDISAGREESTATUS, COMMENT, TAGGEDUSERS, ...rest } = item; // Extract keys to be renamed
              return {
                ...rest, // Retain all other fields
                'AGREE STEERING TEAM RECO': AGREEDISAGREESTATUS,
                COMMENT,
                TAGGEDUSERS
              };
            } else {
              const { AGREEDISAGREESTATUS, RECOMMENDATION, COMMENT, ...rest } = item; // Extract keys to be renamed
              return {
                ...rest, // Retain all other fields
                'AGREE STEERING TEAM RECO': AGREEDISAGREESTATUS,
                'RECOMMENDATION(MAX 24 CHAR)': RECOMMENDATION, // Add the renamed key
                COMMENT
              };
            }
          }
        });
      };
      const updatedData = updateKeyNames(transformedContent);
      console.log('Transformed content:', updatedData);

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(updatedData);

      XLSX.utils.book_append_sheet(wb, ws, 'VotingData');
      // View only sheet
      let newSheetData = [];
      if (tableName == 'Decision Table') {
        // Final Decisions
        newSheetData = [
          {
            BU: selectedBu,
            Cycle: currentActiveCycle,
            'Decision (24 CHARS)':
              'Decision must be 24 characters or fewer, with no special characters allowed',
            'EFFECTIVE DATE(YYYY-MM-DD)': 'To be entered in YYYY-MM-DD format only',
            Owner: 'Mandatory'
          }
        ];
      } else if (tableName == 'Completion Table') {
        // Execute actions
        newSheetData = [
          {
            BU: selectedBu,
            Cycle: currentActiveCycle,
            'Execution Date': 'Mandatory To be entered in YYYY-MM-DD format only',
            'Completion Status': 'Agree or Disagree to be Entered',
            Comments: 'Mandatory if Completion Status is Disagree'
          }
        ];
      }

      const newWs = XLSX.utils.json_to_sheet(newSheetData);

      XLSX.utils.book_append_sheet(wb, newWs, 'Info(View Only)');

      XLSX.writeFile(wb, `${selectedBu}_Voting_${currentVotingTeam}.xlsx`);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleBulkUpload = async (formData, onUploadProgress) => {
    if (formData) {
      await bulkUploadFile(formData, onUploadProgress)
        .then((res) => {
          if (res) {
            triggerNotification(res.message, 'success');
          }
          setUploadStatusMsg(res);
        })
        .catch((error) => {
          console.log(error, 'err');
          setUploadStatusMsg(error);
        })
        .finally(() => {
          setIsUploadModalOpen(false);
        });
    }
  };
  return (
    <Paper
      elevation={3}
      sx={{
        p: 2,
        borderRadius: '8px',
        backgroundColor: '#FBFCFE',
        boxShadow: '4px 0px 24px -2px #005CBC14'
      }}>
      <DynamicTable
        originalColumns={cleanedFields}
        originalData={data}
        tableName={tableName}
        isTableLoading={isTableLoading}
        isSearch={false}
        isColumnFilter
        isCollapseButton={false}
        density="small"
        showOverLayTable={false}
        showTableText={''}
        downloadExcel
        isGlobalFilter
        handleDownloadData={handleDownload}
        isBulkUpload
        onBulkUploadModalOpen={() => setIsUploadModalOpen(true)}
        isUploadDownloadDisabled={isUploadDownloadDisabled}
        isRowChecked
        checkedRows={checkedRows}
        setCheckedRows={setCheckedRows}
        isColumnVisibleFilter={true}
        showCheckbox={!showCheckbox}
        currentVotingTeam={currentVotingTeam}
      />
      {isUploadModalOpen && (
        <UploadModal
          open={isUploadModalOpen}
          handleOpen={() => setIsUploadModalOpen(true)}
          handleClose={() => setIsUploadModalOpen(false)}
          onUploadFile={handleBulkUpload}
          mode="single"
          userLevel={userLevelUpload}
          currentActiveCycle={currentActiveCycle}
          currentActiveCycleID={currentActiveCycleID}
          userType={userType}
          pageId={'Voting'}
        />
      )}
    </Paper>
  );
};

export default DecisionTable;
